// npm
import React from 'react';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { useMutation } from 'react-query';

// common
import { IMemberProps } from 'common/interface/props';
import { IFindEmailOutput, IFindPasswordOutput, ILoginOutput } from 'components/form/interfaces/member.interface';
import { COUPANG, regexEmail, regexPassword } from 'common/common.constatns';

// atomic component
import Text from 'components/UI/atoms/Text';
import Grid from 'components/UI/atoms/Grid';
import TextField from 'components/UI/atoms/TextField';
import Button from 'components/UI/atoms/Button';

// etc
import { FormError } from 'components/form/form-error';
import { FindEmaildMutation, FindPasswordMutation, LoginMutation } from 'components/form/api/member.api';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function Login({ solType }: IMemberProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate(); // 히스토리 구하기
  const [searchParams] = useSearchParams(); // url param 가져오기

  React.useEffect(() => {
    const sendAddressSeq = searchParams.get('sendAddressSeq');
    const returnAddressSeq = searchParams.get('returnAddressSeq');
    if (sendAddressSeq !== null && returnAddressSeq !== null) {
      sessionStorage.setItem('addressSeq', JSON.stringify({ sendAddressSeq: sendAddressSeq, returnAddressSeq: returnAddressSeq }));
    }
  }, []);

  const { mutate: login } = useMutation(LoginMutation, {
    // mutate 함수가 실행되기 전에 실행
    onMutate: (variables) => {
      // 무한 로딩 화면 생성
      Swal.fire({
        title: `로그인 중...`,
        html: '플레이오토 회원검사를 진행중입니다.', // add html attribute if you want or remove
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });
      //console.log(variables); // 변수확인용
    },
    onSuccess: async (data: ILoginOutput, variables) => {
      // //mutation 실행후 받은 data를 serverState에 넣는 예제
      // // queryKey가 'login'로 시작하는 모든 쿼리 무효화
      // await queryClient.invalidateQueries('login');
      // //login에서 응답받은 data를 react-query에 담음
      // queryClient.setQueryData('login', data);

      await Swal.fire({
        title: '성공',
        html: `${data.message}`,
        icon: 'success',
        confirmButtonText: '확인',
      });

      //로그인 성공시 세션에 저장 (백엔드에서 로그인 성공시 쿠키에다가 jwt 토큰을 넣고, http only 속성때문에 프론트에서 토큰을 가져올 수가 없다. )
      sessionStorage.setItem(
        'userInfo',
        JSON.stringify({
          email: variables.email,
          solType: variables.solType,
        }),
      );
      const referUrl = new URL(window.location.href).searchParams.get('referUrl');

      if (referUrl) {
        navigate(referUrl); //해당 주소로 이동
      } else {
        if (data?.result?.useCount && data?.result?.useCount > 0) {
          navigate(`/${solType.toLowerCase()}/LoginAfterChoice`); //해당 주소로 이동
        } else {
          navigate(`/${solType.toLowerCase()}/job/shop-login`); //해당 주소로 이동
        }
      }
    },
    onError: (error: any) => {
      Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
      // 에러 발생
    },
  });

  const { mutate: findPassword } = useMutation(FindPasswordMutation, {
    // mutate 함수가 실행되기 전에 실행
    onMutate: (variables) => {},
    onSuccess: async (data: IFindPasswordOutput, variables) => {
      await Swal.fire({
        title: '성공',
        html: `입력하신 ${variables.email}로 비밀번호 재설정 이메일이 전송되었습니다.<br/>이메일 인증하기 버튼을 눌러 비밀번호를 변경해주세요`,
        icon: 'success',
        confirmButtonText: '확인',
      });
    },
    onError: (error: any) => {
      Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    },
  });

  const { mutate: findEmail } = useMutation(FindEmaildMutation, {
    // mutate 함수가 실행되기 전에 실행
    onMutate: (variables) => {},
    onSuccess: async (data: IFindEmailOutput, variables) => {
      if (data.success === false) {
        Swal.fire({
          title: '경고',
          html: '일치하는 쿠팡 밴더아이디가 없습니다.<br/> 다시 확인하여 주시기 바랍니다.',
          icon: 'error',
        });
      } else
        await Swal.fire({
          title: '성공',
          html: `${data.result.join('<br/>')}`,
          icon: 'success',
          confirmButtonText: '확인',
        });
    },
    onError: (error: any) => {
      Swal.fire({
        title: '실패',
        html: `${error.message}`,
        icon: 'error',
      });
    },
  });

  // 쇼핑몰 로그인 정보 전송 함수
  async function onSubmit(data: any) {
    const { email, password } = data;
    // useMutation 실행
    login({ email, password, solType });
  }

  // 비밀번호 찾기
  async function onfindPw(e: React.MouseEvent) {
    e.preventDefault();
    const { value: email } = await Swal.fire({
      title: `비밀번호 찾기`,
      input: `email`,
      inputPlaceholder: `이메일 주소를 입력해 주시기 바랍니다.`,
      confirmButtonText: `인증메일 발송`,
    });
    findPassword({ email, solType });
  }

  // 이메일 아이디 찾기
  async function onfindEmail(e: React.MouseEvent) {
    e.preventDefault();
    const { value: vendorId } = await Swal.fire({
      title: `아이디 찾기`,
      input: `text`,
      inputPlaceholder: `쿠팡 업체코드를 입력해 주시기 바랍니다.`,
      confirmButtonText: `확인`,
    });
    findEmail({ vendorId, solType });
  }
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid display="flex" direction="column" alignItems="center" justifyContent="center" height={400}>
          <Text fontSize="28px" fontWeight={500}>
            로그인
          </Text>
          <Grid width={400} top={30} bottom={30}>
            <TextField type="email" placeholder="이메일 ID" {...register(`email`, { required: true, maxLength: 50, pattern: regexEmail })} />
            {errors?.email && errors.email && errors.email.type === 'required' && <FormError errorMessage={'이메일은 필수 입력 항목입니다.'} />}
            {errors?.email && errors.email.type === 'maxLength' && <FormError errorMessage={'이메일의 최대 길이를 초과하였습니다.(최대 50자)'} />}
            {errors?.email && errors.email.type === 'pattern' && <FormError errorMessage={'올바른 이메일 형식이 아닙니다.'} />}
            <Grid top={30} />
            <TextField type="password" placeholder="비밀번호" {...register(`password`, { required: true, minLength: 8, pattern: regexPassword })} />
            {errors?.password && errors.password.type === 'required' && <FormError errorMessage={'비밀번호는 필수 입력 항목입니다.'} />}
            {errors?.password && errors.password.type === 'minLength' && <FormError errorMessage={'비밀번호는 8자 이상 입력해 주세요.'} />}
            {errors?.password && errors.password.type === 'pattern' && <FormError errorMessage={'비밀번호는 영문+숫자+특문 8자 이상 입력해 주세요.'} />}
          </Grid>
          <Button type="submit" width={240} color="main">
            로그인
          </Button>

          <Grid display="flex" justifyContent="center" width={400} top={30}>
            <Text underline fontWeight={500} onClick={onfindPw}>
              <a href={`/${solType.toLowerCase()}/member/login`}>비밀번호를 잊으셨나요?</a>
            </Text>
          </Grid>

          {solType === COUPANG && (
            <Grid display="flex" justifyContent="center" width={400} top={10}>
              <Text underline fontWeight={500} onClick={onfindEmail}>
                <a href={`/${solType.toLowerCase()}/member/login`}>아이디를 잊으셨나요?</a>
              </Text>
            </Grid>
          )}
        </Grid>
      </form>
    </>
  );
}
